import {
	CHIP_THEME,
	Chips as ChipsV2,
	COLOR,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useMemo } from 'react';
import styled from 'styled-components';
import { ISideTabItemProps } from '@dealstack/frontend/shared/components/listOld/SideTabItem/SideTabItem.types';
import { IChipsV2Props } from './Chips.v2.types';

export enum SIGNED_CHIP_VARIANT {
	SIGNED = 'signed',
	REJECTED = 'rejected',
	EXPIRED = 'expired',
	NOT_SIGNED = 'not_signed',
	NOT_UPLOADED = 'not_uploaded',
	NOT_APPLICABLE = 'not_applicable',
	PENDING_COUNTER_SIGNATURE = 'pending_counter_signature',
	NOT_SUBMITTED = 'not_submitted',
	WAITING_FOR_WITNESS_SIGNATURES = 'waiting_for_witness_signatures',
	SUBMITTED = 'submitted',
	IN_PROGRESS = 'in_progress',
	COMPLETE = 'complete',
}

interface BadgeConfig {
	color: string;
	textColor: string;
}

const variantToBadgeConfig: Record<SIGNED_CHIP_VARIANT, BadgeConfig> = {
	[SIGNED_CHIP_VARIANT.SIGNED]: {
		color: 'teal.8',
		textColor: 'teal.8',
	},
	[SIGNED_CHIP_VARIANT.COMPLETE]: {
		color: 'teal.8',
		textColor: 'teal.8',
	},
	[SIGNED_CHIP_VARIANT.SUBMITTED]: {
		color: 'teal.8',
		textColor: 'teal.8',
	},
	[SIGNED_CHIP_VARIANT.WAITING_FOR_WITNESS_SIGNATURES]: {
		color: 'orange.8',
		textColor: 'orange.8',
	},
	[SIGNED_CHIP_VARIANT.REJECTED]: { color: 'red.6', textColor: 'red.6' },
	[SIGNED_CHIP_VARIANT.EXPIRED]: { color: 'red.6', textColor: 'red.6' },
	[SIGNED_CHIP_VARIANT.NOT_SIGNED]: {
		color: 'orange.8',
		textColor: 'orange.8',
	},
	[SIGNED_CHIP_VARIANT.NOT_UPLOADED]: {
		color: 'orange.8',
		textColor: 'orange.8',
	},
	[SIGNED_CHIP_VARIANT.NOT_SUBMITTED]: {
		color: 'orange.8',
		textColor: 'orange.8',
	},
	[SIGNED_CHIP_VARIANT.PENDING_COUNTER_SIGNATURE]: {
		color: 'gray.4',
		textColor: 'dark.8',
	},
	[SIGNED_CHIP_VARIANT.NOT_APPLICABLE]: {
		color: 'gray.4',
		textColor: 'dark.8',
	},
	[SIGNED_CHIP_VARIANT.IN_PROGRESS]: {
		color: 'orange.8',
		textColor: 'orange.8',
	},
};

export interface Props {
	variant: SIGNED_CHIP_VARIANT;
}

export const SignedChip = ({ variant }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const config = variantToBadgeConfig[variant];

	return (
		<StatusBadge
			text={tCommonUi(`contracts.signed.${variant}`)}
			color={config.color}
			textColor={config.textColor}
		/>
	);
};

export const StatusChip = styled(
	({
		variant,
		...props
	}: Omit<IChipsV2Props, 'variant'> & { variant: ISideTabItemProps['status'] }) => {
		const [color, prefixIcon] = useMemo(() => {
			switch (variant) {
				case 'success':
					return [COLOR.GREEN, 'checkmark-circle-contained-filled'];
				case 'error':
					return [COLOR.RED, 'minus-circle'];
				case 'warning':
					return [COLOR.YELLOW, 'exclamation-circle'];
				case 'waiting':
					return [COLOR.YELLOW, 'dots-horiz'];
				default:
					return [COLOR.GREY, ''];
			}
		}, [variant]);

		return (
			<ChipsV2
				{...props}
				color={color}
				prefixIcon={prefixIcon}
				chipTheme={CHIP_THEME.LIGHT}
			/>
		);
	}
)`
	.svg {
		margin-right: 0 !important;
	}
`;
